// ---------------------------------------------------------------------------
// usageUtils — pure aggregation helpers for the dataset usage dashboard.
//
// The usage API returns rows aggregated per (bucket × category × action ×
// application). These helpers fold those rows into the headline summary, the
// category/application breakdowns, and the zero-filled weekly trend the UI
// renders. Kept side-effect free so they are unit-testable without React or a
// query client.
// ---------------------------------------------------------------------------

import {
  DATASET_USAGE_RANGE_DAYS,
  DATASET_USAGE_WEEK_DAYS,
} from '../../../constants/index.js';
import type {
  DatasetUsageCategory,
  DatasetUsageRecord,
} from '../../../services/dataHubService.interface.js';

import type {
  UsageBreakdownColor,
  UsageBreakdownItem,
  UsageSummary,
  UsageWeeklyTotal,
} from './DatasetUsageSection.interface.js';

/** Milliseconds in one calendar day. */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Short month/day label options for a weekly bucket start (UTC). */
const WEEK_LABEL_OPTIONS: Intl.DateTimeFormatOptions = {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
};

/** Category preference order used to break ties deterministically. */
const USAGE_CATEGORY_ORDER: DatasetUsageCategory[] = [
  'Create',
  'Read',
  'Update',
  'Delete',
];

/** Category → semantic bar color, mirroring the table badge colors. */
const CATEGORY_COLOR: Record<DatasetUsageCategory, UsageBreakdownColor> = {
  Create: 'success',
  Read: 'brand',
  Update: 'warning',
  Delete: 'error',
};

/** Cap on the number of application rows shown in the breakdown. */
const MAX_APPLICATION_ROWS = 6;

/**
 * Fold usage rows into headline KPIs: total actions, the peak per-bucket unique
 * users (unique users are not additive across rows), and the category with the
 * highest total count.
 */
export function summarizeUsage(records: DatasetUsageRecord[]): UsageSummary {
  let totalActions = 0;
  let peakUniqueUsers = 0;
  const countByCategory = new Map<DatasetUsageCategory, number>();

  for (const record of records) {
    totalActions += record.count;
    if (record.uniqueUsers > peakUniqueUsers) {
      peakUniqueUsers = record.uniqueUsers;
    }
    countByCategory.set(
      record.category,
      (countByCategory.get(record.category) ?? 0) + record.count,
    );
  }

  let topCategory: DatasetUsageCategory | null = null;
  let topCount = 0;
  for (const category of USAGE_CATEGORY_ORDER) {
    const count = countByCategory.get(category) ?? 0;
    if (count > topCount) {
      topCount = count;
      topCategory = category;
    }
  }

  return { totalActions, peakUniqueUsers, topCategory };
}

/**
 * Total interactions per CRUD category, highest first, omitting categories with
 * no activity. Answers "what kind of operations does this dataset see".
 */
export function breakdownByCategory(
  records: DatasetUsageRecord[],
): UsageBreakdownItem[] {
  const countByCategory = new Map<DatasetUsageCategory, number>();
  for (const record of records) {
    countByCategory.set(
      record.category,
      (countByCategory.get(record.category) ?? 0) + record.count,
    );
  }
  return USAGE_CATEGORY_ORDER.filter(
    (category) => (countByCategory.get(category) ?? 0) > 0,
  )
    .map((category) => ({
      label: category,
      count: countByCategory.get(category) ?? 0,
      color: CATEGORY_COLOR[category],
    }))
    .sort((a, b) => b.count - a.count);
}

/**
 * Total interactions per calling application, highest first, capped to the top
 * few. Answers "which surfaces drive usage of this dataset".
 */
export function breakdownByApplication(
  records: DatasetUsageRecord[],
): UsageBreakdownItem[] {
  const countByApplication = new Map<string, number>();
  for (const record of records) {
    countByApplication.set(
      record.application,
      (countByApplication.get(record.application) ?? 0) + record.count,
    );
  }
  return [...countByApplication.entries()]
    .map(([label, count]) => ({
      label,
      count,
      color: 'brand' as const,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_APPLICATION_ROWS);
}

/** UTC midnight (epoch ms) of the calendar day containing `date`. */
function utcDayStart(date: Date): number {
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Fold the sparse daily rows into a dense, zero-filled series of fixed 7-day
 * buckets covering the query range, oldest→newest. Days with no returned row
 * genuinely mean zero interactions, so zero-filling is factual (not estimated);
 * only `count` is summed — `uniqueUsers` is deliberately excluded because it is
 * not additive across rows. The newest bucket ends on `referenceDate`.
 */
export function toWeeklyTotals(
  records: DatasetUsageRecord[],
  referenceDate: Date = new Date(),
  rangeDays: number = DATASET_USAGE_RANGE_DAYS,
): UsageWeeklyTotal[] {
  const weekMs = DATASET_USAGE_WEEK_DAYS * MS_PER_DAY;
  const numWeeks = Math.ceil(rangeDays / DATASET_USAGE_WEEK_DAYS);
  const today = utcDayStart(referenceDate);
  const newestWeekStart = today - (DATASET_USAGE_WEEK_DAYS - 1) * MS_PER_DAY;
  const oldestStart = newestWeekStart - (numWeeks - 1) * weekMs;

  const counts = new Array<number>(numWeeks).fill(0);
  for (const record of records) {
    const day = utcDayStart(new Date(record.date));
    const index = Math.floor((day - oldestStart) / weekMs);
    if (index >= 0 && index < numWeeks) {
      counts[index] = (counts[index] ?? 0) + record.count;
    }
  }

  return counts.map((count, index) => {
    const start = new Date(oldestStart + index * weekMs);
    return {
      weekStart: start.toISOString(),
      label: start.toLocaleDateString('en-US', WEEK_LABEL_OPTIONS),
      count,
    };
  });
}
